// CANONICAL SOURCE — flare/reporters/next/lib/flare.ts
// Copied verbatim into each Next app as `lib/flare.ts` — a src/lib/flare.ts
// path in apps using a src/ layout; see reporters/apps.json for which.
// Do not edit a copy. Edit this file, re-copy, and run
// `node scripts/check-reporters.mjs` from the flare repo to prove the fleet agrees.

// Every name below is written as a full, static `process.env.X` member
// expression on BOTH sides of `??` — Next's build-time inliner only replaces
// that exact shape, never a dynamic or aliased access. That matters because
// this same file ships in two contexts: imported from instrumentation.ts it
// runs in real Node, where every one of these is a real env var; imported
// from global-error.tsx ("use client") it ends up in the BROWSER bundle,
// where only the NEXT_PUBLIC_ names get inlined to literals at build time —
// the bare names are never inlined and read back `undefined` at runtime, so
// without this fallback flareEnabled() is permanently false for every
// React-render error the boundary catches. The NEXT_PUBLIC_ values leak
// nothing new: FlareClient already ships them to the browser directly.
const ENDPOINT = process.env.FLARE_URL ?? process.env.NEXT_PUBLIC_FLARE_URL;
const KEY = process.env.FLARE_KEY ?? process.env.NEXT_PUBLIC_FLARE_KEY;
const RELEASE = process.env.VERCEL_GIT_COMMIT_SHA ?? process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? null;

const TIMEOUT_MS = 2_000;
const MAX_CONTEXT_CHARS = 8_000;

export type FlareKind = "server" | "client" | "job" | "soft";

/**
 * Three independent reasons to do nothing, checked on every call rather than
 * once at import: a missing key, a missing endpoint, or a non-production
 * deployment. Local dev and Vercel preview would otherwise flood the queue with
 * noise the auto-fix runner would then try to fix.
 */
export function flareEnabled(): boolean {
  const env = process.env.VERCEL_ENV ?? process.env.NEXT_PUBLIC_VERCEL_ENV;
  return Boolean(ENDPOINT) && Boolean(KEY) && env === "production";
}

/**
 * `context` is the one free-form field, so it is bounded by SERIALIZED length.
 * Over the limit it is dropped and replaced with a marker rather than truncated:
 * a clipped JSON string is unparseable, so a truncated context would be strictly
 * worse than none, and the marker lets triage say "something was discarded"
 * instead of implying the reporter sent nothing.
 */
export function boundContext(context: Record<string, unknown>): Record<string, unknown> | null {
  if (!context || Object.keys(context).length === 0) return null;
  let serialized: string;
  try {
    serialized = JSON.stringify(context);
  } catch {
    return { _flareDiscarded: "context was not serializable" };
  }
  if (typeof serialized !== "string") return { _flareDiscarded: "context was not serializable" };
  if (serialized.length > MAX_CONTEXT_CHARS) {
    return { _flareDiscarded: `context was ${serialized.length} chars, over the ${MAX_CONTEXT_CHARS} limit` };
  }
  return context;
}

/**
 * BEARER-TOKEN PATHS. `/invite/<token>`, `/s/<token>` and friends carry a
 * credential in the URL itself: whoever reads the path can redeem the invite or
 * open the share. A pathname is otherwise exactly what makes an error
 * triageable, so this redacts only the one segment that IS the secret.
 *
 * A token route lives at `/<prefix>/[token]`, root only, with a prefix below;
 * flare's `scripts/check-reporters.mjs` parses these two array literals out of
 * this file and fails any app whose `[token]` dir sits anywhere else, so the
 * list and the routes cannot drift apart. Keep them plain string literals.
 * TOKEN_ROUTE_STATIC_SEGMENTS are real static siblings (`/invite/error`), not
 * tokens. Pure on purpose: this file also ships in the browser bundle.
 */
export const TOKEN_ROUTE_PREFIXES: readonly string[] = ["a", "c", "invite", "s", "survey", "t", "w"];
export const TOKEN_ROUTE_STATIC_SEGMENTS: readonly string[] = ["error"];

/** `/invite/abc/accept?x#y` → `/invite/[token]/accept?x#y`; also on an absolute URL. */
export function redactTokenPath(url: string): string {
  const origin = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/[^/?#]*/.exec(url)?.[0] ?? "";
  const rest = url.slice(origin.length);
  const cut = rest.search(/[?#]/);
  const path = cut === -1 ? rest : rest.slice(0, cut);
  const tail = cut === -1 ? "" : rest.slice(cut);
  const m = /^\/([^/]+)\/([^/]+)([\s\S]*)$/.exec(path);
  if (!m) return url;
  const [, prefix, seg, after] = m;
  if (!TOKEN_ROUTE_PREFIXES.includes(prefix)) return url;
  if (seg === "[token]" || TOKEN_ROUTE_STATIC_SEGMENTS.includes(seg)) return url;
  return `${origin}/${prefix}/[token]${after}${tail}`;
}

/**
 * A FAILED DRIZZLE QUERY CARRIES ITS BOUND VALUES. `DrizzleQueryError` builds
 * `Failed query: <sql>\nparams: <values>`, and the values are people's emails,
 * plan names and tokens. Keep the SQL, which is what makes the error
 * triageable; drop the params line. On a stack, which opens with that same
 * message, cut only up to the first frame so the frames survive.
 *
 * An EMPTY params line loses only its label: a wrapper that appends the cause
 * (`params: : SERVER_ERROR: ...`) leaves the cause as the only real detail.
 * With values there is no line end to trust, since a value can hold a newline,
 * so everything up to the first frame goes.
 */
export function sanitizeForFlare(text: string): string {
  const start = text.indexOf("\nparams:");
  if (start === -1) return text;
  const rest = text.slice(start + "\nparams:".length);
  const empty = /^ ?(?=$|\n|: )/.exec(rest);
  if (empty) return text.slice(0, start) + sanitizeForFlare(rest.slice(empty[0].length));
  const frames = text.indexOf("\n    at ", start);
  return text.slice(0, start) + (frames === -1 ? "" : text.slice(frames));
}

export type FlarePayload = {
  kind: FlareKind;
  name: string;
  message: string;
  stack: string | null;
  url: string | null;
  release: string | null;
  environment: "production";
  occurredAt: number;
  context: Record<string, unknown> | null;
  userRef: string | null;
  sessionId: string | null;
};

export type FlareIdentity = { userRef: string | null; sessionId: string | null };

/**
 * WHO HIT THE ERROR, AND IN WHICH TAB. FlareClient mints `flare_sid` per tab and,
 * for an app that opts in, looks up the signed-in user's id once as `flare_uid`;
 * both live in sessionStorage and are mirrored to session cookies of the same
 * names so the SERVER half can read them off the request.
 *
 * Given a `Cookie` header (server), it parses that; given nothing, it reads the
 * browser's sessionStorage, and on the server returns nulls. Never throws:
 * Safari's private mode, a sandboxed iframe or a blocked-storage setting all
 * throw on touch, and an exception here would replace the app's real error.
 * Ingest validates both values (no emails, a session-id shape), so this only
 * finds them.
 */
export function readIdentity(cookieHeader?: string | null): FlareIdentity {
  const out: FlareIdentity = { userRef: null, sessionId: null };
  try {
    if (cookieHeader !== undefined) {
      for (const part of (cookieHeader ?? "").split(";")) {
        const eq = part.indexOf("=");
        if (eq === -1) continue;
        const name = part.slice(0, eq).trim();
        if (name !== "flare_uid" && name !== "flare_sid") continue;
        let value: string;
        try {
          value = decodeURIComponent(part.slice(eq + 1).trim());
        } catch {
          continue;
        }
        if (value) out[name === "flare_uid" ? "userRef" : "sessionId"] = value;
      }
      return out;
    }
    if (typeof window === "undefined") return out;
    out.userRef = window.sessionStorage.getItem("flare_uid") || null;
    out.sessionId = window.sessionStorage.getItem("flare_sid") || null;
  } catch {
    // Blocked storage: report without identity rather than not at all.
  }
  return out;
}

const idValue = (v: unknown): string | null =>
  typeof v === "string" && v ? v : typeof v === "number" && Number.isFinite(v) ? String(v) : null;

/**
 * THE DIAGNOSIS AN ERROR CARRIES ON ITSELF, WHICH THIS REPORTER USED TO THROW
 * AWAY.
 *
 * `buildPayload` reads `name`, `message` and `stack` and nothing else, so an
 * error class whose whole purpose is to carry structured detail arrived at
 * flare as a bare sentence. glimpse's `AudioEncodeFailedError` is the case that
 * exposed it: its own doc comment says "the config rides in `params` to flare
 * with the original error kept as `cause`", and neither ever left the browser.
 * A render died on an iPhone on 2026-09-09 with `descriptionBytes` — the one
 * field that says whether the AAC config reached the decoder, and therefore the
 * whole difference between a known bug and a new one — sitting on the thrown
 * error, unreported.
 *
 * WebCodecs errors carry no cause of their own, which is exactly why the app
 * wraps them; a reporter that drops the wrapper's payload undoes the wrapping.
 *
 * Both keys are `_`-prefixed so they cannot collide with a caller's own context,
 * and the cause chain is bounded at three links — an ordinary wrap is one or
 * two, and a cycle is the reason this counts rather than recurses freely. The
 * whole thing still passes through `boundContext`, so a large `params` is
 * discarded with a marker rather than truncated into unparseable JSON.
 */
export function errorDetail(error: Error): Record<string, unknown> {
  const detail: Record<string, unknown> = {};

  const params = (error as Error & { params?: unknown }).params;
  // A plain object only. An array or a scalar `params` is somebody else's field
  // by that name, not a diagnostic payload.
  if (params !== null && typeof params === "object" && !Array.isArray(params)) {
    detail._params = params;
  }

  const chain: Array<{ name: string; message: string }> = [];
  const seen = new Set<unknown>();
  let cause: unknown = (error as Error & { cause?: unknown }).cause;
  while (cause instanceof Error && chain.length < 3 && !seen.has(cause)) {
    seen.add(cause);
    // NAME AND MESSAGE, NOT THE STACK. A cause's stack is minified build output
    // whose frames are the same chunk the top-level `stack` already names, and
    // it is long enough to push the whole context over the limit — trading the
    // field that matters for one that does not.
    chain.push({ name: cause.name || "Error", message: sanitizeForFlare(cause.message || "") });
    cause = (cause as Error & { cause?: unknown }).cause;
  }
  if (chain.length > 0) detail._cause = chain;

  return detail;
}

export function buildPayload(err: unknown, context: Record<string, unknown> = {}): FlarePayload {
  const { kind, url, userRef, sessionId, ...rest } = context;
  let ids: FlareIdentity = { userRef: idValue(userRef), sessionId: idValue(sessionId) };
  // A caller that knows nothing (global-error.tsx, a client reportError) gets
  // the browser's own; the server half passes the request's cookies explicitly.
  if (!ids.userRef || !ids.sessionId) {
    const own = readIdentity();
    ids = { userRef: ids.userRef ?? own.userRef, sessionId: ids.sessionId ?? own.sessionId };
  }
  const error = err instanceof Error ? err : new Error(typeof err === "string" ? err : "Unknown error");
  return {
    kind: (typeof kind === "string" ? kind : "server") as FlareKind,
    name: error.name || "Error",
    message: sanitizeForFlare(error.message || "") || "Unknown error",
    stack: typeof error.stack === "string" ? sanitizeForFlare(error.stack) : null,
    url: typeof url === "string" ? redactTokenPath(url) : null,
    release: RELEASE,
    environment: "production",
    occurredAt: Date.now(),
    context: boundContext({ ...rest, ...errorDetail(error) }),
    userRef: ids.userRef,
    sessionId: ids.sessionId,
  };
}

async function send(body: unknown): Promise<void> {
  if (!flareEnabled()) return;
  try {
    await fetch(ENDPOINT as string, {
      method: "POST",
      headers: { "content-type": "application/json", "x-flare-key": KEY as string },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(TIMEOUT_MS),
      cache: "no-store",
      keepalive: true,
    });
  } catch {
    // Swallowed on purpose. A monitoring library that breaks the app it
    // monitors is worse than no monitoring at all.
  }
}

export async function reportError(err: unknown, context: Record<string, unknown> = {}): Promise<void> {
  try {
    await send(buildPayload(err, context));
  } catch {
    // Unreachable in practice; present so no caller can ever receive a throw.
  }
}

export async function reportSoft(message: string, context: Record<string, unknown> = {}): Promise<void> {
  try {
    await send(buildPayload(new Error(message), { kind: "soft", ...context }));
  } catch {
    // As above.
  }
}
